import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { Themes, isDarkTheme } from '@mlb/play-ui';
import { Card, Skeleton } from '@gif/shared-ui';
import { noop } from '@gif/shared-global';

import { FeaturedIcon, FeaturedIconBorders, SponsorshipTag } from '..';
import { Game } from '../../types/Games';

export interface GameCardProps {
  game?: Game;
  openInNewTab?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  isLoading?: boolean;
}

const StyledCard = styled(Card)<{ $theme: any }>`
  position: relative;
  cursor: pointer;
  text-decoration: none;
  color: ${({ $theme }) => $theme.fontColor};
  background-color: ${({ $theme }) => $theme.cardBgColor};
`;

const StyledIcon = styled(FeaturedIcon)`
  width: 4.5rem;
  height: 4.5rem;
  align-self: center;
`;

const StyledSponsorshipTag = styled(SponsorshipTag)`
  position: absolute;
  left: 1rem;
  bottom: 0.75rem;
`;

const LineClampStyles = styled.div`
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`;

const ContentContainer = styled.section`
  max-width: 12.5rem;
  padding-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Title = styled(LineClampStyles)`
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.0175rem;
  padding-top: 0.5rem;
  line-height: 1.31rem;
`;

const Description = styled(LineClampStyles)`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.13rem;
`;

const IconSkeleton = styled(Skeleton)`
  align-self: center;
`;

const TitleSkeleton = styled(Skeleton)`
  margin: 1rem 0;
`;

const SponsorSkeleton = styled(Skeleton)`
  margin-top: 1rem;
`;

export function GameCard({
  game,
  openInNewTab,
  onClick = noop,
  isLoading = false,
}: GameCardProps) {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card>
        <IconSkeleton width="4.5rem" height="4.5rem" borderRadius="0.5rem" />
        <TitleSkeleton width="50%" height="1rem" />
        <div>
          <Skeleton height="1rem" />
          <Skeleton height="1rem" />
        </div>
        <SponsorSkeleton height="1rem" />
      </Card>
    );
  }

  if (!game) {
    return null;
  }

  const logo = game.logo;
  const sponsorImage = game.sponsorImage;
  const title = game.title;

  return (
    <StyledCard
      forwardedAs={Link}
      to={game.uri}
      $theme={theme}
      {...(openInNewTab && { target: '_blank' })}
      onClick={(e) => onClick(e)}
      data-game-id={game.gameId}
    >
      <StyledIcon
        src={isDarkTheme(theme) ? logo.darkUrl : logo.lightUrl}
        // Due to accessibility, these images can have empty alt text so it doesn't repeat the title
        alt={logo.altText || ''}
        border={
          isDarkTheme(theme)
            ? FeaturedIconBorders.LIGHT
            : FeaturedIconBorders.DARK
        }
      />
      <ContentContainer>
        <Title as="h3">{title}</Title>
        <Description as="p">{game.description}</Description>
      </ContentContainer>
      <StyledSponsorshipTag
        imgURL={
          isDarkTheme(theme) ? sponsorImage?.darkUrl : sponsorImage?.lightUrl
        }
        altText={sponsorImage?.altText || `${title} sponsor`}
        textTheme={isDarkTheme(theme) ? Themes.DARK : Themes.LIGHT}
      />
    </StyledCard>
  );
}

export default GameCard;
